<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { analysesApi, type Analysis } from '@/services/api/analyses';
import { useAppConfirm } from '@/composables/confirm';
import { useNotifier } from '@/composables/notifier';
import ProjectsSubNav from './ProjectsSubNav.vue';

const route = useRoute();
const analyses = ref<Analysis[]>([]);
const confirm = useAppConfirm();
const notify = useNotifier();

function projectIdParam(): string {
  const raw = route.params.id;
  return Array.isArray(raw) ? (raw[0] ?? '') : raw ?? '';
}

async function reload(): Promise<void> {
  analyses.value = await analysesApi.index(projectIdParam());
}

async function remove(analysis: Analysis): Promise<void> {
  const ok = await confirm({
    header: 'Remove Analysis',
    message: 'Are you sure you want to remove this analysis?',
  });
  if (!ok) return;
  await analysesApi.archive(projectIdParam(), analysis.id);
  analyses.value = analyses.value.filter((a) => a.id !== analysis.id);
  notify.success('Analysis archived');
}

async function clone(analysis: Analysis): Promise<void> {
  const cloned = await analysesApi.clone(projectIdParam(), analysis.id);
  analyses.value = [...analyses.value, cloned];
  notify.success('Analysis copied');
}

onMounted(reload);
</script>

<template>
  <div>
    <ProjectsSubNav :project-id="projectIdParam()" />
    <section class="resource">
      <header class="resource__header">
        <h1>Analyses</h1>
        <RouterLink
          :to="{ name: 'projectAnalysisAdd', params: { id: projectIdParam() } }"
          class="resource__cta"
        >
          + New Analysis
        </RouterLink>
      </header>

      <ul class="resource__list">
        <li v-for="a in analyses" :key="a.id">
          <span>{{ a.name }}</span>
          <button type="button" @click="clone(a)">Clone</button>
          <button type="button" @click="remove(a)">Remove</button>
        </li>
      </ul>
      <p v-if="analyses.length === 0">No analyses.</p>
    </section>
  </div>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.resource__cta {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border-radius: 0.375rem;
  text-decoration: none;
}
</style>
